import { Injectable } from '@angular/core';
import { LicenseANPR } from '@classes/anpr/licenseANPR';
import { ClientSmartHub } from '@classes/client/client';
import { environment } from '@environments/environment';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LicenseType } from '@classes/anpr/licenseType';
import { LicenseStatus } from '@classes/anpr/licenseStatus';
import { map } from 'rxjs/operators';
import { CallANPR } from '@classes/anpr/callANPR';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import moment from 'moment';
import * as FileSaver from 'file-saver';
import { Pagination } from '@classes/pagination/pagination';

@Injectable({
  providedIn: 'root',
})
export class LicenseService {
  constructor(private http: HttpClient) {}

  // -- These 3 variables are only used for exporting an excel --
  callListChanged = new Subject<any[]>();
  paginationSubject = new BehaviorSubject<Pagination>(null);
  calls = [];
  // ----------------------------------------------------

  private lastQueryObj: {
    from: null;
    to: null;
    state: null;
  };

  getAllLicenses(): Observable<LicenseANPR[]> {
    return this.http
      .get<LicenseANPR[]>(`${environment.apiUrl}/anpr/licenses`, {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      })
      .pipe(
        map((licenses) => {
          return licenses.map((license) => LicenseANPR.fromObject(license));
        })
      );
  }

  getAllCalls(): Observable<CallANPR[]> {
    return this.http.get<CallANPR[]>(`${environment.apiUrl}/anpr/calls`, {
      headers: {
        authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
      },
    });
  }

  getAllTypes(): Observable<LicenseType[]> {
    return this.http.get<LicenseType[]>(
      `${environment.apiUrl}/anpr/licenses/type`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  getAllStatus(): Observable<LicenseStatus[]> {
    return this.http.get<LicenseStatus[]>(
      `${environment.apiUrl}/anpr/licenses/status`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  addLicense(newLicense: LicenseANPR) {
    return this.http.post(`${environment.apiUrl}/anpr/licenses`, newLicense, {
      headers: {
        authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
      },
    });
  }

  updateLicense(license: LicenseANPR) {
    return this.http.put(`${environment.apiUrl}/anpr/licenses`, license, {
      headers: {
        authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
      },
    });
  }

  endLicense(licenseId: string) {
    return this.http.put(
      `${environment.apiUrl}/anpr/licenses/${licenseId}/end`,
      licenseId,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  getClientLicense(idClient: string): Observable<LicenseANPR> {
    return this.http.get<LicenseANPR>(
      `${environment.apiUrl}/clients/${idClient}/license`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  getClientCalls(idClient: string): Observable<CallANPR[]> {
    return this.http.get<CallANPR[]>(
      `${environment.apiUrl}/clients/${idClient}/calls`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  useAnpr(file: File): Observable<CallANPR> {
    const formData = new FormData();
    formData.append('fichier', file);
    return this.http.post<CallANPR>(
      `${environment.apiUrl}/anpr/call`,
      formData,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  answer(callId: string, correct: boolean): Observable<CallANPR> {
    return this.http.put<CallANPR>(
      `${environment.apiUrl}/anpr/call/${callId}/answer`,
      correct,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      }
    );
  }

  getSource(callId: string): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}/anpr/call/${callId}/source`, {
        observe: 'response',
        responseType: 'blob',
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      })
      .pipe(map((res) => res.body));
  }

  getResult(callId: string): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}/anpr/call/${callId}/resultat`, {
        observe: 'response',
        responseType: 'blob',
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      })
      .pipe(map((res) => res.body));
  }

  dataUrlToFile(dataUrl, fileName) {
    var arr = dataUrl.split(','),
      mime = arr[0].match(/:(.*?):/),
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  getAnprCallsWithPagination(request: PaginationRequest) {
    let httpParams = new HttpParams();

    if (request.pageSize && request.page) {
      httpParams = httpParams
        .append('pageSize', request.pageSize.toString())
        .append(
          'page',
          request.query !== this.lastQueryObj ? '1' : request.page.toString()
        );
    }

    if (request.query) {
      const filters = request.query;

      const filtersQuery = [];

      if (filters.state) {
        filtersQuery.push(`state==${filters.state.value}`);
      }

      if (filters.from && filters.to) {
        filtersQuery.push(
          `date>=${moment(filters.from)
            .startOf('day')
            .utc()
            .format()
            .toString()}`
        );
        filtersQuery.push(
          `date<=${moment(filters.to).endOf('day').utc().format().toString()}`
        );
      }

      httpParams = httpParams.append('Filters', filtersQuery.join());

      this.lastQueryObj = request.query;
    }

    if (request.sort) {
      httpParams = httpParams.append('sorts', request.sort);
    }

    this.http
      .get<any[]>(`${environment.apiUrl}/anpr/calls`, {
        params: httpParams,
        observe: 'response',
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      })
      .subscribe((res: HttpResponse<any[]>) => {
        this.calls = res.body;
        this.callListChanged.next([...this.calls]);
        const pagination: Pagination = JSON.parse(
          res.headers.get('X-Pagination')
        );
        this.paginationSubject.next({ ...pagination });
      });
  }

  exportAnprCall(request: PaginationRequest) {
    let httpParams = new HttpParams();
    if (request.query) {
      const filters = request.query;
      const filtersQuery = [];
      if (filters.from && filters.to) {
        filtersQuery.push(
          `date>=${moment(filters.from)
            .startOf('day')
            .utc()
            .format()
            .toString()}`
        );
        filtersQuery.push(
          `date<=${moment(filters.to).endOf('day').utc().format().toString()}`
        );
      }

      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    return this.http
      .get(`${environment.apiUrl}/anpr/excel`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
        headers: {
          authorization: `Bearer ${localStorage.getItem(environment.tokenKey)}`,
        },
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body, res.headers.get('filename'));
    return res.body;
  }
}
